import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { deliveryAPI, PropertyData } from '../../services/deliveryAPI';
import { parseApiError } from '../../utils/apiErrors';
import PropertyAccessForm, { DROP_OPTIONS } from './PropertyAccessForm';
import useBackGuard from '../../hooks/useBackGuard';

/**
 * Profile: "Delivery access" - the onboarding answers, editable (Brad, 2026-09-23: "These
 * should be in the profile for the customer to modify if needed. Add new photos, change
 * lock codes"). Loads what they told us and, like the address block above it, shows a
 * SHORT SUMMARY with one button; the form opens only on "Change delivery access" (Brad,
 * same day: "Its a lot of info just scrolling open every time someone comes in here").
 * A link that lands on #delivery-access opens the form straight away. Photos save the
 * moment they are added or removed; the answers save on one button.
 */
const DeliveryAccessSection: React.FC = () => {
  const location = useLocation();
  const [form, setForm] = useState<PropertyData | null>(null);
  const [savedForm, setSavedForm] = useState<PropertyData | null>(null);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [editing, setEditing] = useState(location.hash === '#delivery-access');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    deliveryAPI.getProperty().then((p) => { setForm(p || {}); setSavedForm(p || {}); })
      .catch(() => { setForm({}); setSavedForm({}); });
    deliveryAPI.getPropertyPhotos().then((ps) => setPhotoCount(ps.length)).catch(() => setPhotoCount(null));
  }, []);

  useEffect(() => {
    if (location.hash === '#delivery-access') {
      setEditing(true);
      const el = document.getElementById('delivery-access');
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    }
  }, [location.hash]);

  const save = async () => {
    if (!form) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const next = await deliveryAPI.saveProperty(form);
      setForm(next || form);
      setSavedForm(next || form);
      setSaved(true);
      setEditing(false);
    } catch (e) {
      setError(parseApiError(e, { fallback: 'Could not save your delivery access details. Please try again.' }).message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setForm(savedForm); setEditing(false); setError(''); };

  // THE BACK BUTTON ON A HALF-FILLED FORM (Brad, 2026-09-23): a back press with unsaved
  // changes stays here and asks, instead of dropping everything.
  const dirty = editing && form !== null && savedForm !== null && JSON.stringify(form) !== JSON.stringify(savedForm);
  const guard = useBackGuard(dirty);
  const leaveWithoutSaving = () => { setForm(savedForm); setEditing(false); setError(''); guard.allowLeave(); };

  // The summary: what a driver would read first, in a few lines.
  const summary = (p: PropertyData) => {
    const rows: { label: string; value: string }[] = [];
    const drop = DROP_OPTIONS.find((o) => o.v === p.drop_location);
    rows.push({ label: 'Bags go', value: drop ? drop.l.replace(' (add a photo)', '') + (p.drop_location === 'garage' && p.garage_code ? ' · code saved' : '') : 'Not set' });
    if (p.feed_storage_location) rows.push({ label: 'Feed goes', value: p.feed_storage_location });
    if (p.gate_location || p.gate_code) rows.push({ label: 'Gate', value: [p.gate_location, p.gate_code ? 'code saved' : ''].filter(Boolean).join(' · ') });
    rows.push({ label: 'Dog', value: p.dog_present ? `Yes${p.dog_loose ? ', sometimes loose' : ''}${p.dog_notes ? ` · ${p.dog_notes}` : ''}` : 'No' });
    if (photoCount !== null) rows.push({ label: 'Photos', value: photoCount === 0 ? 'None yet' : `${photoCount}` });
    return rows;
  };

  return (
    <div id="delivery-access" className="profile-card" style={{ marginTop: '1.5rem' }}>
      <h3 style={{ margin: '0 0 0.25rem' }}>Delivery access</h3>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', opacity: 0.8 }}>
        Where the bags go, gates and codes, the dog, where things are, and photos for your driver.
        Your delivery day and card are on your <Link to="/bridge-club/delivery" style={{ color: '#B91C1C', fontWeight: 600 }}>Subscription page</Link>.
      </p>
      {form === null || savedForm === null ? (
        <p style={{ color: '#6b7280' }}>Loading…</p>
      ) : !editing ? (
        <>
          <div className="profile-details">
            {summary(savedForm).map((r) => (
              <div key={r.label} className="detail-row">
                <span className="detail-label">{r.label}</span>
                <span className="detail-value">{r.value}</span>
              </div>
            ))}
          </div>
          {saved && <p style={{ color: '#166534', fontWeight: 600, margin: '0.5rem 0 0' }}>Saved.</p>}
          <button type="button" onClick={() => { setEditing(true); setSaved(false); }} className="edit-btn">Change delivery access</button>
        </>
      ) : (
        <>
          {guard.blocked && (
            <div role="alert" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: '#92400e' }}>You have unsaved changes.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" onClick={() => { guard.stay(); save(); }} disabled={saving}
                  style={{ flex: 1, minHeight: 44, border: 'none', borderRadius: 8, background: '#B91C1C', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  Save changes
                </button>
                <button type="button" onClick={leaveWithoutSaving} disabled={saving}
                  style={{ flex: 1, minHeight: 44, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
                  Leave without saving
                </button>
              </div>
            </div>
          )}
          <PropertyAccessForm form={form} onChange={(next) => { setForm(next); setSaved(false); }} showExistingPhotos />
          {error && <div className="error-message" style={{ marginTop: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" onClick={cancel} disabled={saving}
              style={{ flex: 1, minHeight: 44, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving}
              style={{ flex: 1, minHeight: 44, border: 'none', borderRadius: 8, background: '#B91C1C', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DeliveryAccessSection;
